import { GRADUATED_GRADE } from 'constants/gradeYears';
import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';
import { ensureObject } from 'utils/objects';
import { ensureString } from 'utils/string';

export const getIndicatorTypeAnalyticsStudentCountsList = ({
  version = 'v2',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type/analytics/student-counts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorTypeAnalyticsStudentCountsInSchoolList = ({
  version = 'v2',
  params = {},
  schoolUuid,
} = {}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type/analytics/student-counts/school/${schoolUuid}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkMarkCompleteStudentIndicators = ({
  indicatorTypeUuid,
  indicatorId,
  payload: data = {},
  version = 'v3',
} = {}) =>
  client
    .request({
      url: `${version}/indicators/indicator-types/${indicatorTypeUuid}/indicators/${indicatorId}/mark-as-complete/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentIndicatorVerifications = ({
  params = {},
  version = 'v2',
} = {}) =>
  // Fetch the student indicators that are used with verifications.
  client
    .request({
      url: `${version}/indicators/student-indicator-verifications/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getVerificationStatusCounts = ({
  params = {},
  version = 'v2',
} = {}) =>
  // Get the aggregated counts for each verification status.
  client
    .request({
      url: `${version}/indicators/student-indicator-verifications/counts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getVerificationIndicatorTypes = ({
  version = 'v3',
  params = {},
} = {}) =>
  // Fetch the indicator types that are used with verifications.
  client
    .request({
      url: `${version}/indicators/indicator-types/verification_types/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const uploadStudentIndicatorFile = ({
  fileUploadIndicatorId,
  fileKey,
  shortCode,
  version = 'v2',
  userId = '',
} = {}) => {
  /** Upload a file by giving its hashed value. */
  const objectId = shortCode || fileUploadIndicatorId;
  return client
    .request({
      url: `${version}/indicators/student-file-upload-indicators/${objectId}/upload/`,
      method: RequestMethod.POST,
      data: { file: fileKey, shortCode, userId },
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const fetchFileUploadIndicatorList = ({ params = {}, version = 'v2' }) =>
  /** Fetch file upload indicators. */
  client
    .request({
      url: `${version}/indicators/student-file-upload-indicators/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const fetchFileUploadIndicatorDetails = ({
  fileUploadIndicatorId,
  params = {},
  version = 'v2',
}) =>
  /** Fetch an individual file upload indicator. */
  client
    .request({
      url: `${version}/indicators/student-file-upload-indicators/${fileUploadIndicatorId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const fileUploadIndicatorAction = ({
  fileUploadIndicatorId,
  action,
  payload = {},
  version = 'v2',
} = {}) =>
  /**
   * Performs an action on a file upload indicator.
   *
   * Action can be verify, decline, or reset.
   */
  client
    .request({
      url: `${version}/indicators/student-file-upload-indicators/${fileUploadIndicatorId}/${action}/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const generateFileUploadShortCode = ({
  fileUploadIndicatorId,
  version = 'v2',
}) =>
  /** Generate a short code for mobile upload to a file upload indicator. */
  client
    .request({
      url: `${version}/indicators/student-file-upload-indicators/${fileUploadIndicatorId}/short-code/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAnalyticsContext = ({ params = {}, version = 'v2' }) =>
  client
    .request({
      url: `${version}/indicators/analytics-context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * For more info, check the planning doc: https://www.notion.so/schoolinkshome/Student-dashboard-page-level-optimization-2b6d112776464ef0abf0967933494e0d?pvs=4#ce4f6da1ca574285b8f571408658add4
 */
export const getStudentIndicatorCount = ({
  version = 'v3',
  params = {},
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator/student/${studentId}/indicator-count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns student indicators list
 * For more info, check the planning doc: https://www.notion.so/schoolinkshome/Student-dashboard-page-level-optimization-2b6d112776464ef0abf0967933494e0d?pvs=4#ce4f6da1ca574285b8f571408658add4
 */
export const getStudentIndicatorList = ({
  version = 'v3',
  params = {},
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator/student/${studentId}/indicator-list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns student indicator details
 * For more info, check the planning doc: https://www.notion.so/schoolinkshome/Student-dashboard-page-level-optimization-2b6d112776464ef0abf0967933494e0d?pvs=4#57aca4497f9f4f5289f8020b53e9f0bd
 */
export const getStudentIndicatorDetails = ({
  version = 'v3',
  studentId,
  studentIndicatorId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator/student/${studentId}/student-indicator-details/${studentIndicatorId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorBreakoutList = ({
  version = 'v2',
  indicatorUuid,
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-analytics/${indicatorUuid}/breakout-list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorBreakout = ({
  version = 'v2',
  indicatorUuid,
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-analytics/${indicatorUuid}/breakout/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorBreakoutForAllGrades = ({
  version = 'v2',
  indicatorTypeUuid,
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type-analytics/${indicatorTypeUuid}/breakout/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorBreakoutListForAllGrades = ({
  version = 'v2',
  indicatorTypeUuid,
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type-analytics/${indicatorTypeUuid}/breakout-list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorAnalyticsStudentCount = ({
  version = 'v2',
  indicatorUuid,
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-analytics/${indicatorUuid}/students-count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(result => ({
      ...ensureObject(result),
      data: {
        ...ensureObject(result?.data),
        indicatorUuid,
      },
    }))
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorBreakoutOptions = ({
  version = 'v2',
  indicatorUuid,
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-analytics/${indicatorUuid}/breakout-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicators = ({ version = 'v2', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/indicator-analytics/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorsCount = ({ version = 'v2', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/indicator-analytics-count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorBreakoutOptionsForAllGrades = ({
  version = 'v2',
  indicatorTypeUuid,
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type-analytics/${indicatorTypeUuid}/breakout-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentIndicatorTabularList = ({
  version = 'v3',
  params = {},
  data = {},
}) =>
  client
    .request({
      url: `${version}/indicators/tabular-view/list/`,
      method: RequestMethod.POST,
      params,
      data: {
        ...data,
        includesAlumni: ensureString(params?.grades).includes(GRADUATED_GRADE),
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUsersSchoolPermissions = ({ version = 'v3', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/indicator-plan-context-view/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentIndicatorTabularListCount = ({
  version = 'v3',
  params = {},
  data = {},
}) =>
  client
    .request({
      url: `${version}/indicators/tabular-view/list-count/`,
      method: RequestMethod.POST,
      params,
      data: {
        ...data,
        includesAlumni: ensureString(params?.grades).includes(GRADUATED_GRADE),
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentIndicatorTabularIndicatorMap = ({
  version = 'v3',
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/tabular-view/indicator-map/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentIndicatorChangeHistory = ({
  version = 'v3',
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/student-indicators/history-list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHyperMonitoringConfigList = ({ version = 'v3', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/hyper-monitor-config/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHyperMonitoringConfigCreate = ({
  version = 'v3',
  params = {},
  data = {},
}) =>
  client
    .request({
      url: `${version}/indicators/hyper-monitor-config/`,
      method: RequestMethod.POST,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHyperMonitoringConfigUpdate = ({
  version = 'v3',
  params = {},
  data = {},
  id,
}) =>
  client
    .request({
      url: `${version}/indicators/hyper-monitor-config/${id}/`,
      method: RequestMethod.PATCH,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHyperMonitoringConfigDelete = ({
  version = 'v3',
  params = {},
  data = {},
  id,
}) =>
  client
    .request({
      url: `${version}/indicators/hyper-monitor-config/${id}/`,
      method: RequestMethod.DELETE,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentIndicatorMeetReasonTemplate = ({
  version = 'v3',
  params = {},
  studentIndicatorId,
}) =>
  client
    .request({
      url: `${version}/indicators/student-indicators/${studentIndicatorId}/reason-template/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFAFSAIndicator = ({ params = {}, version = 'v2' }) =>
  client
    .request({
      url: `${version}/indicators/indicators/FAFSAIndicator/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

// Endpoint to retrieve indicator information for a student from a class name
export const getIndicatorByClassName = ({
  params = {},
  version = 'v2',
  studentId,
  className,
}) =>
  client
    .request({
      url: `${version}/indicators/student/${studentId}/from-class/${className}`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTabularViewStudentsHash = ({
  version = 'v3',
  params = {},
  data = {},
}) =>
  client
    .request({
      url: `${version}/indicators/tabular-view/student-hash/`,
      method: RequestMethod.POST,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadStudentIndicatorListCSV = ({
  version = 'v3',
  params = {},
  data = {},
}) =>
  client
    .request({
      url: `${version}/indicators/tabular-view/student-indicator-csv/`,
      method: RequestMethod.POST,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorHistogramReportAvailable = ({
  version = 'v3',
  params = {},
  data = {},
}) =>
  client
    .request({
      url: `${version}/indicators/student-indicators/histogram-report-available/`,
      method: RequestMethod.POST,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorHistogramReport = ({
  version = 'v3',
  params = {},
  data = {},
}) =>
  client
    .request({
      url: `${version}/indicators/student-indicators/histogram-report/`,
      method: RequestMethod.POST,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorScoreAverageValue = ({
  version = 'v3',
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/student-support-data-indicators/average-value/`,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorRequirementCluster = ({
  version = 'v3',
  params = {},
  clusterId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-requirement-clusters/${clusterId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorScoreBreakdown = ({ version = 'v3', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/student-support-data-indicators/breakdown/`,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorRequirementClusterGroups = ({
  version = 'v3',
  params = {},
  clusterId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-requirement-clusters/${clusterId}/groups/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentSupportDataIndicator = ({
  version = 'v3',
  params = {},
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/student-support-data-indicators/student/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTotalRequirementClusterPdfReport = ({
  version = 'v3',
  params = {},
  clusterId,
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-requirement-clusters/${clusterId}/pdf-report/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndividualRequirementClusterPdfReport = ({
  version = 'v3',
  params = {},
  groupId,
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-requirement-groups/${groupId}/pdf-report/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRequirementClusterGroupSupportData = ({
  version = 'v3',
  params = {},
  groupId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-requirement-groups/${groupId}/support-data/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const superuserAssessAllIndicators = ({
  version = 'v3',
  params = {},
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/super-admin-tools/assess-all-indicators/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const superuserGetStudentIndicators = ({
  version = 'v3',
  params = {},
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/super-admin-tools/get-all-indicators/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const superuserGetStudentIndicatorsJson = ({
  version = 'v3',
  params = {},
  studentId,
}) =>
  client
    .request({
      url: `${version}/indicators/super-admin-tools/get-all-indicators-json/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlanScopeDetails = ({ version = 'v2', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/indicator-type/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlanScopeDetailsByIndicatorPlan = ({
  version = 'v2',
  params = {},
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlanScopeDetailsBySchool = ({
  version = 'v2',
  params = {},
  schoolUuid,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type/school/${schoolUuid}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorDetailsByIndicatorPlan = ({
  version = 'v2',
  params = {},
  indicatorPlanId,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-type/indicator-plan/${indicatorPlanId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentToDos = ({ version = 'v2', params = {}, studentId }) =>
  client
    .request({
      url: `${version}/indicators/student/${studentId}/indicators/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorPlans = ({ version = 'v3', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/indicator-plans/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorPlan = ({ version = 'v3', params = {}, id }) =>
  client
    .request({
      url: `${version}/indicators/indicator-plans/${id}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateIndicatorPlan = ({
  version = 'v3',
  params = {},
  payload,
  id,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-plans/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteIndicatorPlan = ({
  version = 'v3',
  params = {},
  payload,
  id,
}) =>
  client
    .request({
      url: `${version}/indicators/indicator-plans/${id}/`,
      method: RequestMethod.DELETE,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const copyIndicatorPlan = ({ version = 'v3', params = {}, id }) =>
  client
    .request({
      url: `${version}/indicators/indicator-plans/${id}/copy/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createIndicatorPlan = ({ version = 'v3', params = {}, payload }) =>
  client
    .request({
      url: `${version}/indicators/indicator-plans/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIndicatorPlansCount = ({ version = 'v3', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/indicator-plans/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .then(res => ({ data: res?.data?.length }))
    .catch(handleError);

export const getIndicatorContext = ({ version = 'v2', params = {} }) =>
  client
    .request({
      url: `${version}/indicators/context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGuardianRemindersCount = ({ version = 'v1', params = {} }) =>
  client.request({
    url: `${version}/sl_users/guardian/reminders/reminders-count/`,
    params,
  });

export const getGuardianReminders = ({ version = 'v1', params = {} }) =>
  client.request({
    url: `${version}/sl_users/guardian/reminders/`,
    params,
  });
